import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  showConfirmationPrompt,
  showDialog,
  showErrorToast,
  showSuccessToast,
} from '@bpinternal/ui-kit-next'
import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { Copy, Edit, ExternalLink, Trash2, Zap } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { getCdmStudioUrl } from '~/shared'
import { useLocalStore } from '~/stores'
import { BotIcon } from '../../../components'
import { useDeleteBot, useQueryKeys, useRenameBotDialog, useUpdateAlwaysAlive, useUsage } from '../../../hooks'
import { showUpsellDialog, useQuery } from '../../../services'
import { showBotDeletionPrompt } from '../../bots/utils/botDeletionPrompt'
import { CopyToBotDialog } from './CopyToBotDialog'

type Props = {
  className?: string
  children: ReactNode
  setAlwaysAlivePending?: (value: boolean) => void
  useCopyToBot?: boolean
  workspaceId: string
  botId: string
  userId: string
  botName: string
}

export const BotCardDropdown = ({ className, children, botId, workspaceId, botName, ...props }: Props) => {
  const router = useRouter()

  const { data: bots = [] } = useQuery('workspaces_/$workspaceId_/bots_', { workspaceId })
  const { mutate: deleteBot } = useDeleteBot(workspaceId)
  const { data: bot } = useQuery('workspaces_/$workspaceId_/bots_/$botId_', { botId, workspaceId })
  const { openRenameDialog } = useRenameBotDialog({ botId, workspaceId, currentBotName: botName })

  const { always_alive: alwaysAliveUsage } = useUsage({ workspaceId, quotas: ['always_alive'] })

  const isAlwaysAlive = bot?.alwaysAlive
  const alwaysAliveDisabled = alwaysAliveUsage.value >= alwaysAliveUsage.quota && !isAlwaysAlive

  const { mutate: updateAlwaysAlive, isPending: alwaysAlivePending } = useUpdateAlwaysAlive()

  const [open, setOpen] = useState(false) // Need to controle the state for now because of a bug in radix, see: https://github.com/radix-ui/primitives/issues/837#issuecomment-2716644471

  const queryClient = useQueryClient()
  const { removeLastActiveBotId } = useLocalStore.getState()

  const queryKeysUsage = useQueryKeys().getWorkspaceUsage

  const { setAlwaysAlivePending } = props

  useEffect(() => {
    if (setAlwaysAlivePending) {
      setAlwaysAlivePending(alwaysAlivePending)
    }
  }, [alwaysAlivePending, setAlwaysAlivePending])

  const copyToBot = async (targetBotId: string, targetBotName: string) => {
    if (!bot) {
      return
    }

    setOpen(false)
    showDialog(
      ({ close }) => (
        <CopyToBotDialog
          close={close}
          workspaceId={workspaceId}
          sourceBotId={bot.id}
          targetBotId={targetBotId}
          sourceBotName={bot.name}
          targetBotName={targetBotName}
        />
      ),
      {
        title: `Copying to ${targetBotName}`,
      }
    )
  }
  const openInStudio = () => {
    window.open(getCdmStudioUrl(botId))
    setOpen(false)
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent onClick={(e) => e.stopPropagation()} className={className} align="end">
        <DropdownMenuItem
          onClick={() => {
            setOpen(false)
            openRenameDialog()
          }}
        >
          <Edit className="mr-2 h-4 w-4" />
          <span>Rename</span>
        </DropdownMenuItem>

        <DropdownMenuItem onClick={openInStudio}>
          <ExternalLink className="mr-2 h-4 w-4" />
          <span>Open In Studio</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => {
            if (alwaysAliveDisabled) {
              setOpen(false)
              showUpsellDialog({
                quota: 'always_alive',
                workspaceId,
              }).then(
                () =>
                  void router.navigate({
                    to: '/workspaces/$workspaceId/billing',
                    params: { workspaceId },
                  })
              )
            } else {
              setOpen(false)
              showConfirmationPrompt(
                <span>
                  Always Alive ensures the Agent runs continuously, enabling faster response times when users first
                  initiate a conversation
                </span>,
                {
                  title: isAlwaysAlive ? 'Disable Always Alive' : 'Enable Always Alive',
                }
              ).then(() =>
                updateAlwaysAlive({
                  botId,
                  workspaceId,
                  alwaysAlive: !isAlwaysAlive,
                })
              )
            }
          }}
        >
          <Zap className="mr-2 h-4 w-4" />
          <span>{isAlwaysAlive ? 'Disable Always Alive' : 'Enable Always Alive'}</span>
        </DropdownMenuItem>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="data-[disabled]:text-gray-10" disabled={bots.length <= 1}>
            <Copy className="mr-2 h-4 w-4" />
            <span>Copy to bot</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {bots
              .filter((bot) => bot.id !== botId)
              .map((bot) => (
                <DropdownMenuItem key={bot.id} onClick={() => copyToBot(bot.id, bot.name)}>
                  <BotIcon id={bot.id} size={'2'} />
                  <span>{bot.name}</span>
                </DropdownMenuItem>
              ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-danger-600 focus:bg-danger-100 focus:text-danger-700"
          onClick={() => {
            setOpen(false)

            showBotDeletionPrompt(botName).then((confirmed) => {
              if (confirmed) {
                deleteBot({
                  id: botId,
                  options: {
                    onSuccess: () => {
                      showSuccessToast(`The bot "${botName}" has been successfully deleted`)
                      removeLastActiveBotId(workspaceId)
                      return queryClient.invalidateQueries({ queryKey: queryKeysUsage(workspaceId) })
                    },
                    onError: () => {
                      showErrorToast(`Failed to delete the bot "${botName}"`)
                      return Promise.resolve()
                    },
                  },
                })
              }
            })
          }}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          <span>Delete</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
